/* Showcase map — a real street map for the public demo.
   OpenStreetMap tiles through Leaflet (already in the app): no API key, no Google
   billing however many strangers open the landing page. Fixed on the Illawarra where the
   software actually runs, non-interactive, with simulated trucks driving the routes. */
import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { CITY, SITES, routeTo } from "@/lib/showcaseCity";

const ORANGE = "#F57C00";
// Plant, centre and haul corridors all follow whichever market city we detected, so a
// visitor in Houston watches the trucks run their own suburbs.
const PLANT = CITY.plant;
const CENTER = CITY.center;
const ROUTES = SITES.map((s, i) => ({ id: `r${i + 1}`, name: s.name, pts: routeTo(s) }));

const FIT = [CITY.plant, ...ROUTES.flatMap((r) => r.pts)];

const FLEET = [
  { no: "SP-07", route: "r1", t0: 0.18, sp: 0.020, tint: ORANGE },
  { no: "SP-11", route: "r2", t0: 0.55, sp: 0.014, tint: "#3B82F6" },
  { no: "SP-03", route: "r3", t0: 0.88, sp: 0.004, tint: "#22C55E" },
  { no: "SP-14", route: "r4", t0: 0.34, sp: 0.017, tint: ORANGE },
  { no: "SP-09", route: "r5", t0: 0.70, sp: -0.018, tint: "#71717A" },
];

function ptAt(pts, t) {
  const c = Math.min(1, Math.max(0, t));
  const segs = [];
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const d = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    segs.push(d); total += d;
  }
  let want = c * total;
  for (let i = 0; i < segs.length; i++) {
    if (want <= segs[i] || i === segs.length - 1) {
      const f = segs[i] ? want / segs[i] : 0;
      const [y1, x1] = pts[i]; const [y2, x2] = pts[i + 1];
      return { lat: y1 + (y2 - y1) * f, lng: x1 + (x2 - x1) * f, a: (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI * -1 };
    }
    want -= segs[i];
  }
  const l = pts[pts.length - 1];
  return { lat: l[0], lng: l[1], a: 0 };
}

const truckIcon = (no, tint, angle) => L.divIcon({
  className: "",
  iconSize: [0, 0],
  html: `<div style="position:relative;transform:translate(-50%,-50%)">
    <div style="position:absolute;left:-13px;top:-13px;width:26px;height:26px;border-radius:50%;background:${tint};opacity:.2"></div>
    <div style="transform:rotate(${angle}deg);width:22px;height:13px;margin:-6px 0 0 -11px;background:${tint};
      border:1.5px solid #fff;border-radius:2px;box-shadow:0 1px 4px rgba(0,0,0,.45)"></div>
    <div style="position:absolute;left:0;top:-30px;transform:translateX(-50%);white-space:nowrap;
      font:700 10px/1 ui-monospace,monospace;letter-spacing:.5px;color:#202124;
      -webkit-text-stroke:3px #fff;paint-order:stroke">${no}</div>
    <div style="position:absolute;left:0;top:-30px;transform:translateX(-50%);white-space:nowrap;
      font:700 10px/1 ui-monospace,monospace;letter-spacing:.5px;color:#202124">${no}</div>
  </div>`,
});

const plantIcon = L.divIcon({
  className: "",
  iconSize: [0, 0],
  html: `<div style="position:relative;transform:translate(-50%,-50%)">
    <div style="position:absolute;left:-22px;top:-22px;width:44px;height:44px;border-radius:50%;background:${ORANGE};opacity:.18"></div>
    <div style="width:16px;height:16px;margin:-8px 0 0 -8px;background:${ORANGE};border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.5)"></div>
    <div style="position:absolute;left:0;top:12px;transform:translateX(-50%);white-space:nowrap;
      font:700 9px/1 ui-monospace,monospace;letter-spacing:2px;color:#3c4043;-webkit-text-stroke:3px #fff;paint-order:stroke">PLANT</div>
    <div style="position:absolute;left:0;top:12px;transform:translateX(-50%);white-space:nowrap;
      font:700 9px/1 ui-monospace,monospace;letter-spacing:2px;color:#3c4043">PLANT</div>
  </div>`,
});

const siteIcon = L.divIcon({
  className: "",
  iconSize: [0, 0],
  html: `<div style="transform:translate(-50%,-100%)">
    <svg width="22" height="30" viewBox="0 0 22 30"><path d="M11 29C4 19.5 1 16 1 11a10 10 0 0 1 20 0c0 5-3 8.5-10 18z"
      fill="#D93025" stroke="#fff" stroke-width="1.5"/><circle cx="11" cy="11" r="3.6" fill="#fff"/></svg>
  </div>`,
});

/* Frame the whole haul area — cities differ in spread, so a fixed zoom either crops
   Dallas or leaves Dublin lost in a sea of tiles. */
function FitHaul({ points }) {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(L.latLngBounds(points), { padding: [30, 30], animate: false });
  }, [map, points]);
  return null;
}

export default function ShowcaseMap() {
  const [t, setT] = useState(0);
  const start = useRef(Date.now());
  useEffect(() => {
    const i = setInterval(() => setT((Date.now() - start.current) / 1000), 120);
    return () => clearInterval(i);
  }, []);

  const trucks = useMemo(() => FLEET.map((f) => {
    const raw = f.t0 + f.sp * t * 0.14;
    const tt = ((raw % 1) + 1) % 1;
    return { ...f, ...ptAt(ROUTES.find((r) => r.id === f.route).pts, tt) };
  }), [t]);

  return (
    <MapContainer center={CENTER} zoom={12} zoomControl={false} attributionControl={false}
      dragging={false} scrollWheelZoom={false} doubleClickZoom={false} touchZoom={false} keyboard={false}
      style={{ height: "100%", width: "100%", background: "#EDE9E3" }}>
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" subdomains={["a", "b", "c"]} maxZoom={19} />
      <FitHaul points={FIT} />
      {/* haul routes, with the selected truck's run picked out in directions-blue */}
      {ROUTES.map((r, i) => (
        <Polyline key={r.id} positions={r.pts}
          pathOptions={{ color: i === 0 ? "#4285F4" : "#5F6368", weight: i === 0 ? 5 : 3.5, opacity: i === 0 ? 0.95 : 0.55 }} />
      ))}
      {ROUTES.map((r) => <Marker key={`${r.id}-s`} position={r.pts[r.pts.length - 1]} icon={siteIcon} interactive={false} />)}
      <Marker position={PLANT} icon={plantIcon} interactive={false} />
      {trucks.map((tk) => (
        <Marker key={tk.no} position={[tk.lat, tk.lng]} icon={truckIcon(tk.no, tk.tint, tk.a)} interactive={false} />
      ))}
    </MapContainer>
  );
}
